import * as THREE from "three";
import { idsMatch, isHumiditySensor } from "../../../components/siteSetupDefaults.js";

export { isHumiditySensor };

export function isHumidityLayoutSensor(layoutSensor) {
    return String(layoutSensor?.type ?? "").toLowerCase() === "humidity";
}

export function temperatureSensorsFromCable(cable) {
    return (cable?.sensorList || []).filter((sensor) => !isHumiditySensor(sensor));
}

function finiteOr(n, fallback) {
    const v = Number(n);
    return Number.isFinite(v) ? v : fallback;
}

/** Map BTS Banner heights onto the compressed temperature-cable Y span. */
function humidityTrackYs(cable, layoutCable, count, site) {
    const n = Math.max(count, 1);
    const tempYs = (site?.layoutSensors || [])
        .filter(
            (row) =>
                row.customerSiteLayoutCableId ===
                    layoutCable?.customerSiteLayoutCableId &&
                !isHumidityLayoutSensor(row)
        )
        .map((row) => Number(row.y))
        .filter((y) => Number.isFinite(y));
    const layoutLo = finiteOr(layoutCable?.bottomPointY, null);
    const layoutHi = finiteOr(layoutCable?.topPointY, null);
    const layoutH = finiteOr(layoutCable?.height, 8);
    const lo =
        tempYs.length > 0
            ? Math.min(...tempYs)
            : layoutLo != null
              ? layoutLo
              : 0;
    const hi =
        tempYs.length > 0
            ? Math.max(...tempYs)
            : layoutHi != null && layoutHi > lo
              ? layoutHi
              : lo + Math.max(layoutH, 8);

    const firstTemp = finiteOr(cable?.firstSensorHeight, lo);
    const tempSpace = finiteOr(cable?.sensorSpacing, 0);
    const tempCount = Math.max(finiteOr(cable?.sensorCount, 1), 1);
    const physTemp0 = firstTemp;
    const physTempStep = tempSpace > 0 ? tempSpace : 4;
    const physTemp1 = physTemp0 + Math.max(tempCount - 1, 1) * physTempStep;

    const firstBanner = finiteOr(cable?.firstBannerSensorHeight, 0);
    const bannerSpace = finiteOr(cable?.bannerSensorSpacing, 0);
    const physHum0 = firstBanner > 0 ? firstBanner : physTemp0;
    const physHumStep = bannerSpace > 0 ? bannerSpace : physTempStep;

    const physSpan = physTemp1 - physTemp0;
    const layoutSpan = hi - lo;
    const physToLayout = (physY) => {
        if (Math.abs(physSpan) < 0.001) return lo;
        return lo + ((physY - physTemp0) / physSpan) * layoutSpan;
    };

    let ys = [];
    for (let i = 0; i < n; i += 1) {
        ys.push(physToLayout(physHum0 + i * physHumStep));
    }

    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    if (minY < lo - 0.01 || maxY > hi + 0.01) {
        const srcSpan = maxY - minY;
        ys = ys.map((y) => {
            if (srcSpan < 0.001) return lo;
            return lo + ((y - minY) / srcSpan) * layoutSpan;
        });
    }
    return ys;
}

export function formatHumiditySensorValueString(sensor) {
    if (sensor?.isActive === false) return "Off";
    if (sensor?.alarmOn) return "CON";

    const tempStr = String(sensor?.temperature ?? "").trim();
    if (tempStr === "-O-" || tempStr === "-o-") return "-O-";

    const emc = Number(sensor?.emc);
    const hasEmc = Number.isFinite(emc) && emc >= 0;
    const tempNum = parseFloat(tempStr);
    const hasTemp =
        tempStr !== "" &&
        tempStr !== "-E-" &&
        tempStr !== "-e-" &&
        Number.isFinite(tempNum);

    const emcLabel = hasEmc ? `${Math.round(emc)}%` : "";
    const tempLabel = hasTemp ? `${Math.round(tempNum)}°` : "";

    if (!hasTemp && hasEmc) return emcLabel;
    if (hasTemp && hasEmc) return `${tempLabel} ${emcLabel}`;
    if (hasTemp) return tempLabel;
    return "-E-";
}

const BTS_HUMIDITY_ORANGE = new THREE.Color(0xffa500);
const BTS_HUMIDITY_ORANGE_RED = new THREE.Color(0xff4500);
const BTS_HUMIDITY_LIGHT_BLUE = new THREE.Color(0xadd8e6);
const BTS_HUMIDITY_CORNFLOWER = new THREE.Color(0x6495ed);

export function humidityLayoutColor(layoutSensor) {
    const emc = Number(layoutSensor?.emc);
    const binEmc = Number.isFinite(Number(layoutSensor?.binEmc))
        ? Number(layoutSensor.binEmc)
        : 0;
    const hasGrain = Number(layoutSensor?.grainHeight) > 0;
    const drying = Number.isFinite(emc) && emc > binEmc;
    if (drying) {
        return hasGrain ? BTS_HUMIDITY_ORANGE_RED.clone() : BTS_HUMIDITY_ORANGE.clone();
    }
    return hasGrain ? BTS_HUMIDITY_CORNFLOWER.clone() : BTS_HUMIDITY_LIGHT_BLUE.clone();
}

function sensorIdKey(sensor) {
    const id = Number(sensor?.id ?? sensor?.sensorId);
    return Number.isFinite(id) ? id : null;
}

function humidityBelongsToCable(sensor, cable) {
    if (!isHumiditySensor(sensor) || !cable) return false;
    if (idsMatch(sensor.alternateCableId, cable.id)) return true;
    const altName = String(sensor.alternateCableName ?? "").trim();
    const cableName = String(cable.name ?? "").trim();
    if (altName && cableName && altName === cableName) return true;
    return Number(sensor.cableId) > 0 && idsMatch(sensor.cableId, cable.id);
}

export function humiditySensorsForCable(cable, bin, site) {
    const out = [];
    const seen = new Set();
    const take = (sensor) => {
        const id = sensorIdKey(sensor);
        if (id == null || seen.has(id) || !isHumiditySensor(sensor)) return;
        seen.add(id);
        out.push(sensor);
    };

    for (const sensor of cable?.sensorList || []) {
        if (isHumiditySensor(sensor)) take(sensor);
    }

    for (const other of bin?.cableList || []) {
        if (other === cable) continue;
        for (const sensor of other.sensorList || []) {
            if (humidityBelongsToCable(sensor, cable)) take(sensor);
        }
    }

    const siteSensors = site?.data?.sensors || site?.sensors || [];
    for (const sensor of siteSensors) {
        if (humidityBelongsToCable(sensor, cable)) take(sensor);
    }

    return out.sort((a, b) => {
        const ap = Number(a.position ?? a.number ?? 0);
        const bp = Number(b.position ?? b.number ?? 0);
        return ap - bp;
    });
}

function findSiteBinForLayoutCable(site, layoutCable) {
    const bins = site?.bins || [];
    const catalogId = layoutCable?.binDataId ?? layoutCable?.binId;
    return (
        bins.find(
            (bin) =>
                bin.binDataId === catalogId ||
                bin.binId === catalogId ||
                Number(bin.binDataId) === Number(catalogId) ||
                Number(bin.binId) === Number(catalogId)
        ) ||
        bins.find(
            (bin) =>
                String(bin.binName ?? "").trim() ===
                String(layoutCable?.binName ?? "").trim()
        ) ||
        null
    );
}

function findSiteCableForLayoutCable(bin, layoutCable, site) {
    const lists = [
        bin?.cableList,
        site?.cables,
        site?.data?.bins?.flatMap((b) => b.cableList || []) || [],
    ];
    for (const list of lists) {
        const found = (list || []).find(
            (cable) =>
                idsMatch(cable.id, layoutCable?.cableId) ||
                (layoutCable?.cableName &&
                    String(cable.name ?? "").trim() ===
                        String(layoutCable.cableName).trim())
        );
        if (found) return found;
    }
    return null;
}

function nextLayoutSensorId(site) {
    const ids = (site?.layoutSensors || [])
        .map((sensor) => sensor.customerSiteLayoutSensorId)
        .filter((n) => Number.isFinite(n));
    return (ids.length ? Math.max(...ids) : 0) + 1;
}

function applyHumidityValues(layoutSensor, sensor, bin) {
    layoutSensor.sensorValueString = formatHumiditySensorValueString(sensor);
    const emc = Number(sensor.emc);
    layoutSensor.sensorValueNumber = Number.isFinite(emc) ? emc : null;
    layoutSensor.type = "Humidity";
    layoutSensor.emc = Number.isFinite(emc) ? emc : null;
    const binEmc = Number(bin?.emc);
    layoutSensor.binEmc = Number.isFinite(binEmc) ? binEmc : 0;
    const grainHeight = Number(bin?.grainHeight ?? bin?.grainWallHeight);
    layoutSensor.grainHeight = Number.isFinite(grainHeight) ? grainHeight : 0;
}

/** Place Banner humidity probes on matching temperature cables (BTS AlternateCableId). */
export function attachHumidityLayoutSensorsToSite(site) {
    if (!site?.layoutCables?.length) return;
    if (!Array.isArray(site.layoutSensors)) site.layoutSensors = [];

    let nextId = nextLayoutSensorId(site);

    for (const layoutCable of site.layoutCables) {
        const bin = findSiteBinForLayoutCable(site, layoutCable);
        const cable = findSiteCableForLayoutCable(bin, layoutCable, site);
        if (!cable) continue;

        const humiditySensors = humiditySensorsForCable(cable, bin, site);
        if (!humiditySensors.length) continue;

        const humidityYs = humidityTrackYs(
            cable,
            layoutCable,
            humiditySensors.length,
            site
        );

        for (let i = 0; i < humiditySensors.length; i += 1) {
            const sensor = humiditySensors[i];
            const sensorId = sensorIdKey(sensor);
            let layoutSensor =
                sensorId != null
                    ? site.layoutSensors.find(
                          (row) => Number(row.sensorId) === sensorId
                      )
                    : null;

            const x =
                Number.isFinite(Number(layoutCable.topPointX))
                    ? Number(layoutCable.topPointX)
                    : Number(layoutCable.x) || 0;
            const z =
                Number.isFinite(Number(layoutCable.topPointZ))
                    ? Number(layoutCable.topPointZ)
                    : Number(layoutCable.z) || 0;
            const humidityY = humidityYs[i];

            if (!layoutSensor) {
                layoutSensor = {
                    customerSiteLayoutSensorId: nextId++,
                    customerSiteLayoutCableId:
                        layoutCable.customerSiteLayoutCableId,
                    customerSiteLayoutId: layoutCable.customerSiteLayoutId,
                    customerSiteLayoutGroupId:
                        layoutCable.customerSiteLayoutGroupId,
                    customerSiteLayoutBinId:
                        layoutCable.customerSiteLayoutBinId,
                    cableOrder: sensor.position ?? sensor.number ?? 0,
                    binId: layoutCable.binId ?? bin?.binDataId ?? bin?.binId,
                    cableId: layoutCable.cableId ?? cable.id,
                    sensorId: sensor.id,
                    sensorName: sensor.name,
                    x,
                    y: humidityY,
                    z,
                };
                site.layoutSensors.push(layoutSensor);
            } else {
                layoutSensor.customerSiteLayoutCableId =
                    layoutCable.customerSiteLayoutCableId;
                layoutSensor.x = x;
                layoutSensor.y = humidityY;
                layoutSensor.z = z;
            }

            applyHumidityValues(layoutSensor, sensor, bin);
        }
    }
}

export function syncHumidityLayoutSensorValues(site) {
    if (!site?.layoutSensors?.length) return;
    for (const layoutSensor of site.layoutSensors) {
        if (!isHumidityLayoutSensor(layoutSensor)) continue;
        const bin = findSiteBinForLayoutCable(site, {
            binId: layoutSensor.binId,
            binDataId: layoutSensor.binId,
        });
        const cable = findSiteCableForLayoutCable(
            bin,
            { cableId: layoutSensor.cableId },
            site
        );
        const sensor = [
            ...(cable?.sensorList || []),
            ...(bin?.cableList || []).flatMap((c) => c.sensorList || []),
            ...(site?.data?.sensors || []),
        ].find((s) => Number(s.id) === Number(layoutSensor.sensorId));
        if (!sensor) continue;
        applyHumidityValues(layoutSensor, sensor, bin);
    }
}
